import { useRef } from 'react';

import { useTelegram } from '@/shared/hooks/useTelegram';
import { cn } from '@/shared/lib/cn';

import styles from './Tabs.module.css';

export interface TabItem {
  value: string;
  label: string;
  badge?: number;
}

export interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  items: TabItem[];
  className?: string;
  'aria-label'?: string;
}

/**
 * Роль tablist без панелей: панели рендерят экраны, поэтому здесь только
 * навигация. Стрелки влево/вправо — требование WAI-ARIA для tablist.
 */
export function Tabs({
  value,
  onChange,
  items,
  className,
  'aria-label': ariaLabel,
}: TabsProps): React.ReactElement {
  const { haptic } = useTelegram();
  const listRef = useRef<HTMLDivElement>(null);

  const move = (delta: number): void => {
    const index = items.findIndex((item) => item.value === value);
    if (index === -1) return;
    const next = items[(index + delta + items.length) % items.length];
    if (!next) return;
    haptic('selection');
    onChange(next.value);
    listRef.current
      ?.querySelector<HTMLButtonElement>(`[data-value="${next.value}"]`)
      ?.focus({ preventScroll: false });
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(styles.list, className)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          move(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          move(-1);
        }
      }}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            data-value={item.value}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            className={cn(styles.tab, active && styles.active)}
            onClick={() => {
              if (active) return;
              haptic('selection');
              onChange(item.value);
            }}
          >
            <span className={styles.label}>{item.label}</span>
            {item.badge !== undefined && item.badge > 0 ? (
              <span className={styles.badge}>{item.badge > 99 ? '99+' : item.badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
